import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

let toastId = 0;

/**
 * Toast provider — manages a stack of toast notifications.
 *
 * Usage:
 *   const { addToast } = useToast();
 *   addToast('Ошибка', 'Не удалось сохранить', 'error');
 */
export function ToastProvider ({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((title, message, type = 'error') => {
    const id = ++toastId;
    setToasts(prev => [...prev.slice(-4), { id, title, message, type }]);
    setTimeout(() => removeToast(id), 10_000);
    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 16,
          right: 16,
          maxWidth: 380,
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          {toasts.map(t => (
            <div
              key={t.id}
              style={{
                padding: '10px 14px',
                borderRadius: 8,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                fontSize: 13,
                boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                color: '#fff',
                background: t.type === 'success'
                  ? 'rgba(22,101,52,0.92)'
                  : t.type === 'warning'
                    ? 'rgba(146,64,14,0.92)'
                    : 'rgba(127,29,29,0.92)',
                animation: 'toast-slide-in 0.2s ease-out'
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>{t.title}</strong>
                {t.message && (
                  <span style={{ marginLeft: 6, opacity: 0.85 }}>{t.message}</span>
                )}
              </div>
              <button
                onClick={() => removeToast(t.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  opacity: 0.6,
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: 16,
                  lineHeight: 1
                }}
                aria-label="Закрыть"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast () {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
